import { useState, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import {
  contractManagementService,
  type ContractDraft,
  type PlaceholderConfigItem,
  type D4signContract,
  type SignerItem,
  type ContractListItem,
  type ContractTag,
  type ContractLogItem,
  type ContractKanbanColumn,
  type DraftKanbanColumn,
} from './services/contract-management.service';
import { GoogleDocsEmbedEditor } from './components/GoogleDocsEmbedEditor';
import {
  ContractDocumentActions,
  ContractDocumentActionMenuItems,
} from './components/ContractDocumentActions';
import { PlaceholderInput } from './components/PlaceholderInput';
import {
  isContractFullySigned,
  isContractPartialSignature,
  isContractSignedOrPartial,
  requiresContaAzulConfirmation,
} from './utils/contract-status';
import { formatNumberWithSuffix, formatCurrencyWithExtenso, getVariablesWithComputed } from './utils/placeholder-utils';
import { Loader2, Trash2, Pencil, FilePlus, ArrowLeft, XCircle, AlertTriangle, Clock, CheckCircle2, MoreHorizontal } from 'lucide-react';
import { config } from '@/config/env.config';
import { useAuthStore } from '@/store/auth.store';
import { googleDocsIntegrationService } from '@/features/google-docs-integration/services/google-docs-integration.service';
import { clientContractsService } from '@/features/client-contracts/services/client-contracts.service';
import { employeesService } from '@/features/employees/services/employees.service';
import { getClientDisplayName } from '@/features/clients/utils/display';

/** Formata previsão de envio: "em breve", "em ~30 min", "às 14:35" */
function formatEstimatedSend(estimatedMinutes?: number): string {
  if (estimatedMinutes == null || estimatedMinutes <= 0) return 'em breve';
  if (estimatedMinutes < 60) return `em ~${estimatedMinutes} min`;
  const h = Math.floor(estimatedMinutes / 60);
  const m = estimatedMinutes % 60;
  if (m === 0) return h === 1 ? 'em ~1 hora' : `em ~${h} horas`;
  return `em ~${h}h ${m}min`;
}

/** Formata horário previsto: "às 14:35" */
function formatEstimatedAt(estimatedAt?: string): string | null {
  if (!estimatedAt) return null;
  try {
    const d = new Date(estimatedAt);
    return `às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return null;
  }
}

/** Ação da assinatura D4Sign: 1=Assinar, 2=Aprovar, 3=Reconhecer, etc. */
// eslint-disable-next-line react-refresh/only-export-components -- constante usada só neste módulo; mover exigiria arquivo novo
export const SIGNER_ACT_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Assinar' },
  { value: 2, label: 'Aprovar' },
  { value: 3, label: 'Reconhecer' },
  { value: 4, label: 'Assinar como parte' },
  { value: 5, label: 'Assinar como testemunha' },
  { value: 6, label: 'Assinar como interveniente' },
  { value: 7, label: 'Acusar recebimento' },
  { value: 8, label: 'Assinar como Emissor, Endossante e Avalista' },
  { value: 9, label: 'Assinar como Emissor, Endossante, Avalista, Fiador' },
  { value: 10, label: 'Assinar como fiador' },
  { value: 11, label: 'Assinar como parte e fiador' },
  { value: 12, label: 'Assinar como responsável solidário' },
  { value: 13, label: 'Assinar como parte e responsável solidário' },
];

const KANBAN_COLUMNS: Array<{
  key: ContractKanbanColumn;
  label: string;
}> = [
  { key: 'em_producao', label: 'Em produção' },
  { key: 'aguardando_assinatura', label: 'Aguardando assinatura' },
  { key: 'assinado', label: 'Assinados' },
  { key: 'cadastrado_sistema_financeiro', label: 'Cadastrado no sistema financeiro' },
];

function getKanbanColumnFromItem(item: ContractListItem): ContractKanbanColumn {
  if (item.kind === 'draft') {
    return ((item.draft.kanbanColumn as DraftKanbanColumn | null) ?? 'em_producao') as ContractKanbanColumn;
  }
  if (item.contract.contaAzulRegisteredAt) return 'cadastrado_sistema_financeiro';
  if (item.contract.kanbanColumn === 'cadastrado_sistema_financeiro') return 'cadastrado_sistema_financeiro';
  if (isContractSignedOrPartial(item.contract)) return 'assinado';
  if (item.contract.kanbanColumn) return item.contract.kanbanColumn as ContractKanbanColumn;
  return 'aguardando_assinatura';
}

/** Rascunho ainda não excluído pode ser removido. */
function canDeleteDraftRow(d: ContractDraft): boolean {
  return !d.deletedAt;
}

/** Contrato D4Sign: não excluir se já totalmente assinado ou já soft-deleted. */
function canDeleteD4signContractRow(c: D4signContract): boolean {
  if (c.deletedAt) return false;
  if (isContractFullySigned(c)) return false;
  return true;
}

function ContractItemActionsMenu({
  children,
  hasActions,
}: {
  children: ReactNode;
  hasActions: boolean;
}) {
  if (!hasActions) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          aria-label="Ações do contrato"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function getLogActionLabel(action: string): string {
  const map: Record<string, string> = {
    'draft.created': 'Rascunho criado',
    'draft.signers_updated': 'Signatários atualizados',
    'draft.send_queued': 'Envio enfileirado',
    'draft.sent_for_signature': 'Enviado para assinatura',
    'draft.tag_added': 'Tag adicionada',
    'draft.tag_removed': 'Tag removida',
    'draft.kanban_moved': 'Movido no kanban',
    'draft.deleted': 'Rascunho removido',
    'contract.created': 'Contrato criado',
    'contract.sent_for_signature': 'Enviado para assinatura',
    'contract.status_updated': 'Status atualizado',
    'contract.signed_by_all': 'Assinado por todos',
    'contract.tag_added': 'Tag adicionada',
    'contract.tag_removed': 'Tag removida',
    'contract.kanban_moved': 'Movido no kanban',
    'contract.conta_azul_marked': 'Marcado no Conta Azul',
    'contract.deleted': 'Contrato removido',
    'contract.client_contract_link_queued': 'Vínculo de cliente enfileirado',
    'contract.employee_contract_link_queued': 'Vínculo de funcionário enfileirado',
    'contract.client_contract_linked': 'Vinculado ao contrato de cliente',
    'contract.employee_contract_linked': 'Vinculado ao contrato de funcionário',
  };
  return map[action] ?? action;
}

export function ContractManagementDashboard({
  googleAccessToken,
  onGoogleLogin,
}: {
  googleAccessToken?: string | null;
  onGoogleLogin?: () => void;
} = {}) {
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [signForm, setSignForm] = useState<{
    wordTemplateId: string;
    nameDocument: string;
    uuidSafe: string;
    uuidFolder: string;
    variables: Record<string, string>;
    signers: SignerItem[];
    message: string;
  }>({
    wordTemplateId: '',
    nameDocument: '',
    uuidSafe: '',
    uuidFolder: '',
    variables: {},
    signers: [],
    message: '',
  });
  /** E-mails extras para assinatura no formulário de criação (fill form). */
  const [extraSigners, setExtraSigners] = useState<SignerItem[]>([]);
  /** Draft selecionado para visualizar/editar (contrato em produção) — abre em tela dedicada */
  const [selectedDraft, setSelectedDraft] = useState<ContractDraft | null>(null);
  /** Preserva posição de scroll ao abrir o draft para evitar scroll para o topo */
  const scrollBeforeDraftRef = useRef(0);
  /** Filtros e paginação da lista de contratos */
  const [contractsPage, setContractsPage] = useState(1);
  const [contractsSearch, setContractsSearch] = useState('');
  const [contractsSearchInput, setContractsSearchInput] = useState('');
  const [contractsStartDate, setContractsStartDate] = useState('');
  const [contractsEndDate, setContractsEndDate] = useState('');
  const [selectedTagFilterId, setSelectedTagFilterId] = useState<string>('all');
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6B7280');
  const [tagManagerDialogOpen, setTagManagerDialogOpen] = useState(false);
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const [addTagTargetItem, setAddTagTargetItem] = useState<ContractListItem | null>(null);
  const [addTagSelectedTagId, setAddTagSelectedTagId] = useState('');
  /** Aba interna de contratos: determina source + status fixo */
  const [contractsInnerTab, setContractsInnerTab] = useState<'all' | 'drafts' | 'signing' | 'signed'>('all');
  const [contractsShowDeleted, setContractsShowDeleted] = useState<'active' | 'deleted' | 'all'>('active');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Mapeia aba interna para source e status enviados à API */
  const innerTabToFilters: Record<
    'all' | 'drafts' | 'signing' | 'signed',
    { sourceFilter: 'all' | 'drafts' | 'd4sign'; statusFilter: string | undefined }
  > = {
    all:     { sourceFilter: 'all',    statusFilter: undefined },
    drafts:  { sourceFilter: 'drafts', statusFilter: undefined },
    signing: { sourceFilter: 'd4sign', statusFilter: '0' },
    signed:  { sourceFilter: 'd4sign', statusFilter: 'signed' },
  };
  const { sourceFilter: contractsSourceFilter, statusFilter: contractsStatusFilter } =
    innerTabToFilters[contractsInnerTab];
  /** Tela de preenchimento de formulário (em tela cheia, não popup) */
  const [fillFormScreenOpen, setFillFormScreenOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('contracts');
  /** Job de envio em andamento (para polling e exibição de status) */
  const [activeSendJobId, setActiveSendJobId] = useState<string | null>(null);
  /** Modal de confirmação antes de excluir rascunho ou contrato D4Sign */
  const [deleteConfirm, setDeleteConfirm] = useState<{
    kind: 'draft' | 'contract';
    id: string;
    name: string;
  } | null>(null);
  const [contaAzulConfirmContract, setContaAzulConfirmContract] = useState<D4signContract | null>(null);

  const { data: wordTemplates = [], isLoading: loadingWord } = useQuery({
    queryKey: ['contract-management-word-templates'],
    queryFn: contractManagementService.listWordTemplates,
  });

  const { data: contractTags = [] } = useQuery({
    queryKey: ['contract-management-tags'],
    queryFn: contractManagementService.listTags,
  });

  const { data: contractsPageData, isLoading: loadingContracts } = useQuery({
    queryKey: [
      'contract-management-contracts',
      contractsPage,
      contractsSearch,
      contractsInnerTab,
      contractsShowDeleted,
      selectedTagFilterId,
      contractsStartDate,
      contractsEndDate,
    ] as const,
    queryFn: () =>
      contractManagementService.listContracts({
        page: contractsPage,
        limit: contractsInnerTab === 'all' ? 100 : 10,
        search: contractsSearch || undefined,
        sourceFilter: contractsSourceFilter,
        statusFilter: contractsStatusFilter,
        showDeleted: contractsShowDeleted,
        tagIds:
          selectedTagFilterId !== 'all' ? [selectedTagFilterId] : undefined,
        startDate: contractsStartDate || undefined,
        endDate: contractsEndDate || undefined,
      }),
  });

  const { isLoading: loadingDrafts } = useQuery({
    queryKey: ['contract-management-drafts'],
    queryFn: contractManagementService.listDrafts,
  });

  const { data: draftDetail } = useQuery({
    queryKey: ['contract-management-draft', selectedDraft?.id],
    queryFn: () => contractManagementService.getDraft(selectedDraft!.id),
    enabled: !!selectedDraft?.id,
  });

  const { data: draftLogs = [] } = useQuery<ContractLogItem[]>({
    queryKey: ['contract-management-draft-logs', selectedDraft?.id],
    queryFn: () => contractManagementService.listDraftLogs(selectedDraft!.id),
    enabled: !!selectedDraft?.id,
  });

  const { data: safes = [] } = useQuery({
    queryKey: ['contract-management-safes'],
    queryFn: contractManagementService.listSafes,
    enabled: fillFormScreenOpen || !!selectedDraft,
  });

  const { data: sendJobStatus } = useQuery({
    queryKey: ['contract-management-send-job', activeSendJobId],
    queryFn: () => contractManagementService.getSendJobStatus(activeSendJobId!),
    enabled: !!activeSendJobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return 2000; // Poll a cada 2s enquanto na fila ou processando
    },
  });

  const { data: googleDocsConfig } = useQuery({
    queryKey: ['google-docs-integration-config'],
    queryFn: () => googleDocsIntegrationService.getConfig(),
  });
  const { data: clientContractsList = [] } = useQuery({
    queryKey: ['contract-management-client-contracts'],
    queryFn: () => clientContractsService.list({}),
    enabled: fillFormScreenOpen || activeTab === 'contracts',
  });
  const { data: employeeContractsList = [] } = useQuery({
    queryKey: ['contract-management-employee-contracts'],
    queryFn: () => employeesService.listContracts({}),
    enabled: fillFormScreenOpen || activeTab === 'contracts',
  });
  const user = useAuthStore((s) => s.user);
  const approverUserIds = Array.isArray(googleDocsConfig?.approverUserIds) ? googleDocsConfig.approverUserIds : [];
  const canApprove = !!user?.id && approverUserIds.includes(user.id);

  const draftForForm = draftDetail ?? selectedDraft;

  useEffect(() => {
    if (!sendJobStatus || !activeSendJobId) return;
    if (sendJobStatus.status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-management-drafts'] });
      toast({ title: 'Documento enviado para assinatura' });
      setActiveSendJobId(null);
      setSelectedDraft(null);
      setSignForm((f) => ({
        ...f,
        wordTemplateId: '',
        nameDocument: '',
        uuidSafe: '',
        uuidFolder: '',
        variables: {},
        signers: [],
        message: '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reage só à mudança de status/job; toast e sendJobStatus completo re-disparariam o efeito
  }, [sendJobStatus?.status, activeSendJobId, queryClient]);

  useEffect(() => {
    if (draftForForm?.id) {
      const signers = Array.isArray(draftForForm.signers) ? draftForForm.signers : [];
      const normalized = signers.map((s) => ({
        email: typeof s.email === 'string' ? s.email : '',
        act: typeof s.act === 'number' ? s.act : Number(s.act) || 1,
      })).filter((s) => s.email);
      setSignForm((prev) => ({ ...prev, signers: normalized }));
    }
  }, [draftForForm?.id, draftForForm?.signers]);

  const updateDraftMutation = useMutation({
    mutationFn: ({ id, signers }: { id: string; signers: SignerItem[] }) =>
      contractManagementService.updateDraft(id, signers),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-management-draft', updated.id] });
    },
  });

  const saveSignersDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!selectedDraft?.id || !draftForForm?.id) return;
    const signersToSave = signForm.signers
      .map((s) => ({ email: (s.email ?? '').trim(), act: s.act ?? 1 }))
      .filter((s) => s.email);
    const fromDraft = Array.isArray(draftForForm.signers) ? draftForForm.signers : [];
    const same =
      signersToSave.length === fromDraft.length &&
      signersToSave.every((s, i) => s.email === (fromDraft[i]?.email ?? '') && s.act === (fromDraft[i]?.act ?? 1));
    if (same) return;
    if (saveSignersDebounceRef.current) clearTimeout(saveSignersDebounceRef.current);
    saveSignersDebounceRef.current = setTimeout(() => {
      updateDraftMutation.mutate({ id: selectedDraft.id, signers: signersToSave });
      saveSignersDebounceRef.current = null;
    }, 800);
    return () => {
      if (saveSignersDebounceRef.current) clearTimeout(saveSignersDebounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate muda a cada render; incluí-lo reiniciaria o debounce
  }, [signForm.signers, selectedDraft?.id, draftForForm?.id, draftForForm?.signers]);

  // Garante que o usuário atual tenha permissão no doc ao abrir um draft (evita conflito de conta Google no embed)
  useEffect(() => {
    if (!selectedDraft?.id) return;
    contractManagementService.addViewerToDraft(selectedDraft.id).catch(() => {});
  }, [selectedDraft?.id]);

  // Restaura posição de scroll ao abrir o draft (evita que a página scroll para o topo)
  useLayoutEffect(() => {
    if (selectedDraft?.embedEditUrl && scrollBeforeDraftRef.current > 0) {
      const y = scrollBeforeDraftRef.current;
      window.scrollTo(0, y);
    }
  }, [selectedDraft?.embedEditUrl]);

  const generateDocForReviewMutation = useMutation({
    mutationFn: (data: {
      wordTemplateId: string;
      nameDocument: string;
      variables: Record<string, string>;
      driveFolderId?: string;
      signers?: SignerItem[];
    }) => contractManagementService.generateDocForReview(data),
    onSuccess: () => {
      setFillFormScreenOpen(false);
      queryClient.invalidateQueries({ queryKey: ['contract-management-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
      toast({ title: 'Documento gerado. Os aprovadores foram notificados e poderão enviar para assinatura.' });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao gerar documento para revisão',
        variant: 'destructive',
      });
    },
  });

  const sendForSignatureMutation = useMutation({
    mutationFn: (data: {
      wordTemplateId: string;
      nameDocument: string;
      variables: Record<string, string>;
      signers: SignerItem[];
      uuidSafe: string;
      uuidFolder?: string;
      message?: string;
      googleDocId?: string;
      draftId?: string;
    }) => contractManagementService.sendForSignature(data),
    onSuccess: (data) => {
      setActiveSendJobId(data.jobId);
      toast({
        title: 'Envio enfileirado',
        description: data.position > 1
          ? `Posição na fila: ${data.position}. O documento será enviado em breve.`
          : 'O documento será enviado em breve.',
      });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao enfileirar envio',
        variant: 'destructive',
      });
    },
  });

  const cancelSendJobMutation = useMutation({
    mutationFn: (jobId: string) => contractManagementService.cancelSendJob(jobId),
    onSuccess: (data) => {
      if (data.cancelled) {
        setActiveSendJobId(null);
        toast({ title: 'Envio cancelado da fila' });
      } else {
        toast({ title: data.message ?? 'Não foi possível cancelar', variant: 'destructive' });
      }
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao cancelar envio',
        variant: 'destructive',
      });
    },
  });

  const deleteWordMutation = useMutation({
    mutationFn: (id: string) => contractManagementService.deleteWordTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-word-templates'] });
      toast({ title: 'Modelo excluído' });
    },
    onError: () => toast({ title: 'Erro ao excluir', variant: 'destructive' }),
  });

  const deleteDraftMutation = useMutation({
    mutationFn: (id: string) => contractManagementService.deleteDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
      setDeleteConfirm(null);
      setSelectedDraft(null);
      toast({ title: 'Rascunho removido da lista (doc permanece no Drive)' });
    },
    onError: (err: any) =>
      toast({
        title: err?.response?.data?.message ?? 'Erro ao remover rascunho',
        variant: 'destructive',
      }),
  });

  const deleteContractMutation = useMutation({
    mutationFn: (id: string) => contractManagementService.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
      setDeleteConfirm(null);
      toast({
        title: 'Contrato removido da lista',
        description: 'O cancelamento foi enviado ao D4Sign quando o documento já estava publicado.',
      });
    },
    onError: (err: any) =>
      toast({
        title: err?.response?.data?.message ?? 'Erro ao excluir contrato',
        variant: 'destructive',
      }),
  });

  const deletePending =
    deleteDraftMutation.isPending || deleteContractMutation.isPending;

  const handleConfirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.kind === 'draft') {
      deleteDraftMutation.mutate(deleteConfirm.id);
    } else {
      deleteContractMutation.mutate(deleteConfirm.id);
    }
  };

  const createTagMutation = useMutation({
    mutationFn: (data: { name: string; color?: string }) =>
      contractManagementService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-tags'] });
      setNewTagName('');
      setNewTagColor('#6B7280');
      toast({ title: 'Tag criada com sucesso' });
    },
    onError: (err: any) =>
      toast({
        title: err?.response?.data?.message ?? 'Erro ao criar tag',
        variant: 'destructive',
      }),
  });

  const deleteTagMutation = useMutation({
    mutationFn: (id: string) => contractManagementService.deleteTag(id),
    onSuccess: (_data, deletedId) => {
      setSelectedTagFilterId((prev) => (prev === deletedId ? 'all' : prev));
      queryClient.invalidateQueries({ queryKey: ['contract-management-tags'] });
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
    },
  });

  const addTagMutation = useMutation({
    mutationFn: (data: { item: ContractListItem; tagId: string }) => {
      if (data.item.kind === 'draft') {
        return contractManagementService.addTagToDraft(data.item.draft.id, data.tagId);
      }
      return contractManagementService.addTagToContract(data.item.contract.id, data.tagId);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
      if (vars.item.kind === 'draft') {
        queryClient.invalidateQueries({
          queryKey: ['contract-management-draft-logs', vars.item.draft.id],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ['contract-management-contract-logs', vars.item.contract.id],
        });
      }
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao adicionar tag',
        variant: 'destructive',
      });
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: (data: { item: ContractListItem; tagId: string }) => {
      if (data.item.kind === 'draft') {
        return contractManagementService.removeTagFromDraft(data.item.draft.id, data.tagId);
      }
      return contractManagementService.removeTagFromContract(data.item.contract.id, data.tagId);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
      if (vars.item.kind === 'draft') {
        queryClient.invalidateQueries({
          queryKey: ['contract-management-draft-logs', vars.item.draft.id],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ['contract-management-contract-logs', vars.item.contract.id],
        });
      }
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao remover tag',
        variant: 'destructive',
      });
    },
  });

  const markContaAzulMutation = useMutation({
    mutationFn: ({
      contractId,
      confirmPartialSignature,
    }: {
      contractId: string;
      confirmPartialSignature?: boolean;
    }) =>
      contractManagementService.markContractAsContaAzulRegistered(contractId, {
        confirmPartialSignature,
      }),
    onSuccess: (_data, { contractId }) => {
      queryClient.invalidateQueries({ queryKey: ['contract-management-contracts'] });
      queryClient.invalidateQueries({
        queryKey: ['contract-management-contract-detail', contractId],
      });
      queryClient.invalidateQueries({
        queryKey: ['contract-management-contract-logs', contractId],
      });
      setContaAzulConfirmContract(null);
      toast({ title: 'Contrato marcado como cadastrado no Conta Azul' });
    },
    onError: (err: any) =>
      toast({
        title: err?.response?.data?.message ?? 'Erro ao marcar no Conta Azul',
        variant: 'destructive',
      }),
  });

  const requestMarkContaAzul = (contract: D4signContract) => {
    if (requiresContaAzulConfirmation(contract)) {
      setContaAzulConfirmContract(contract);
      return;
    }
    markContaAzulMutation.mutate({ contractId: contract.id });
  };

  const openAddTagModal = (item: ContractListItem) => {
    setAddTagTargetItem(item);
    setAddTagSelectedTagId('');
    setAddTagDialogOpen(true);
  };

  const openContractDetail = (contract: D4signContract) => {
    navigate({ to: '/contract-management/contracts/$id', params: { id: contract.id } });
  };

  const renderItemActionsMenu = (
    item: ContractListItem,
    options?: {
      showContaAzulAction?: boolean;
      onDownloadComplete?: () => void;
    },
  ) => {
    const isDeleted =
      item.kind === 'draft' ? !!item.draft.deletedAt : !!item.contract.deletedAt;
    const canAddTag = !isDeleted;
    const canShowDocumentActions = !isDeleted;
    const canMarkContaAzul =
      !isDeleted &&
      item.kind === 'd4sign' &&
      !!options?.showContaAzulAction &&
      isContractSignedOrPartial(item.contract) &&
      !item.contract.contaAzulRegisteredAt;
    const canDelete =
      item.kind === 'draft'
        ? !isDeleted && canDeleteDraftRow(item.draft)
        : !isDeleted && canDeleteD4signContractRow(item.contract);
    const hasActions =
      canAddTag || canShowDocumentActions || canMarkContaAzul || canDelete;

    return (
      <ContractItemActionsMenu hasActions={hasActions}>
        {canAddTag && (
          <DropdownMenuItem
            onSelect={(e) => {
              e.stopPropagation();
              openAddTagModal(item);
            }}
          >
            <FilePlus className="h-4 w-4" />
            Adicionar tag
          </DropdownMenuItem>
        )}
        {canShowDocumentActions && (
          <>
            {canAddTag && <DropdownMenuSeparator />}
            {item.kind === 'draft' ? (
              <ContractDocumentActionMenuItems
                draft={item.draft}
                showDownload={false}
              />
            ) : (
              <ContractDocumentActionMenuItems
                contractId={item.contract.id}
                googleDocId={item.contract.googleDocId}
                googleDocWebViewUrl={item.contract.googleDocWebViewUrl}
                onDownloadComplete={options?.onDownloadComplete}
              />
            )}
          </>
        )}
        {canMarkContaAzul && item.kind === 'd4sign' && (
          <>
            {(canAddTag || canShowDocumentActions) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={(e) => {
                e.stopPropagation();
                requestMarkContaAzul(item.contract);
              }}
            >
              <CheckCircle2 className="h-4 w-4" />
              Marcar Conta Azul
            </DropdownMenuItem>
          </>
        )}
        {canDelete && (
          <>
            {(canAddTag || canShowDocumentActions || canMarkContaAzul) && (
              <DropdownMenuSeparator />
            )}
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={(e) => {
                e.stopPropagation();
                setDeleteConfirm({
                  kind: item.kind === 'draft' ? 'draft' : 'contract',
                  id: item.kind === 'draft' ? item.draft.id : item.contract.id,
                  name:
                    item.kind === 'draft'
                      ? item.draft.nameDocument
                      : item.contract.nameDoc,
                })
              }}
              disabled={deletePending}
            >
              <Trash2 className="h-4 w-4" />
              Excluir
            </DropdownMenuItem>
          </>
        )}
      </ContractItemActionsMenu>
    );
  };

  const addTagAvailableTags = (() => {
    if (!addTagTargetItem) return [] as ContractTag[];
    const usedTags =
      addTagTargetItem.kind === 'draft'
        ? addTagTargetItem.draft.tags ?? []
        : addTagTargetItem.contract.tags ?? [];
    return contractTags.filter((tag) => !usedTags.some((t) => t.id === tag.id));
  })();

  const handleConfirmAddTag = () => {
    if (!addTagTargetItem || !addTagSelectedTagId) return;
    addTagMutation.mutate(
      { item: addTagTargetItem, tagId: addTagSelectedTagId },
      {
        onSuccess: () => {
          setAddTagDialogOpen(false);
          setAddTagTargetItem(null);
          setAddTagSelectedTagId('');
        },
      },
    );
  };

  const selectedWordTemplate = wordTemplates.find((t) => t.id === signForm.wordTemplateId);
  const templateDocFileId = selectedWordTemplate?.googleDocsUrl?.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1] ?? null;

  const {
    data: templatePlaceholdersData,
    isLoading: loadingTemplatePlaceholders,
  } = useQuery({
    queryKey: ['contract-management-template-placeholders', signForm.wordTemplateId, templateDocFileId],
    queryFn: () =>
      contractManagementService.getDocPlaceholders(templateDocFileId!),
    enabled:
      !!signForm.wordTemplateId &&
      !!templateDocFileId &&
      fillFormScreenOpen,
  });

  const placeholderKeys =
    (templatePlaceholdersData?.placeholders?.length ? templatePlaceholdersData.placeholders : null) ??
    selectedWordTemplate?.placeholders ??
    [];

  /** Formata variáveis antes de enviar: número com sufixo vira "N (extenso) sufixo"; valor monetário vira "R$ X (por extenso)". */
  const formatVariablesForSubmit = (
    variables: Record<string, string>,
    placeholderConfig?: Record<string, PlaceholderConfigItem> | null,
  ): Record<string, string> => {
    if (!placeholderConfig) return variables;
    const out = { ...variables };
    for (const [key, config] of Object.entries(placeholderConfig)) {
      if (config?.type === 'number' && (config.suffixSingular ?? config.suffixPlural)) {
        const raw = variables[key] ?? '';
        out[key] = formatNumberWithSuffix(
          raw,
          config.suffixSingular ?? '',
          config.suffixPlural ?? '',
        );
      }
      if (config?.type === 'currency') {
        const raw = variables[key] ?? '';
        out[key] = formatCurrencyWithExtenso(raw);
      }
    }
    return out;
  };

  const handleSendDraft = () => {
    if (!selectedDraft) return;
    if (!signForm.uuidSafe) {
      toast({ title: 'Selecione o cofre D4Sign', variant: 'destructive' });
      return;
    }
    const signers = signForm.signers.filter((s) => s.email?.trim());
    if (signers.length === 0) {
      toast({ title: 'Informe ao menos um signatário (e-mail + ação)', variant: 'destructive' });
      return;
    }
    const template = selectedDraft.wordTemplateId
      ? wordTemplates.find((t) => t.id === selectedDraft.wordTemplateId)
      : undefined;
    const placeholderKeysForTemplate =
      (template?.placeholders?.length ? template.placeholders : null) ?? [];
    const variablesWithComputed = getVariablesWithComputed(
      selectedDraft.variables ?? {},
      template?.placeholderConfig ?? undefined,
      placeholderKeysForTemplate,
    );
    const formattedVariables = formatVariablesForSubmit(
      variablesWithComputed,
      template?.placeholderConfig ?? undefined,
    );
    sendForSignatureMutation.mutate({
      wordTemplateId: selectedDraft.wordTemplateId ?? '',
      nameDocument: selectedDraft.nameDocument,
      variables: formattedVariables,
      signers: signers.map((s) => ({ email: s.email.trim(), act: s.act ?? 1 })),
      uuidSafe: signForm.uuidSafe,
      uuidFolder: signForm.uuidFolder || undefined,
      message: signForm.message || undefined,
      googleDocId: selectedDraft.googleDocId ?? undefined,
      draftId: selectedDraft.id,
    });
  };

  const handleGenerateForReview = () => {
    if (!signForm.wordTemplateId || !signForm.nameDocument.trim()) {
      toast({ title: 'Selecione o modelo e informe o nome do documento', variant: 'destructive' });
      return;
    }
    const variablesWithComputed = getVariablesWithComputed(
      signForm.variables,
      selectedWordTemplate?.placeholderConfig ?? undefined,
      placeholderKeys,
    );
    const formattedVariables = formatVariablesForSubmit(
      variablesWithComputed,
      selectedWordTemplate?.placeholderConfig ?? undefined,
    );
    const config = selectedWordTemplate?.placeholderConfig ?? {};
    const signersFromPlaceholders: SignerItem[] = placeholderKeys
      .filter((key) => config[key]?.type === 'email' && config[key]?.isSigner)
      .map((key) => ({ email: (formattedVariables[key] ?? '').trim(), act: 4 })) // 4 = Assinar como parte
      .filter((s) => s.email);
    const extraFiltered = extraSigners
      .map((s) => ({ email: (s.email ?? '').trim(), act: typeof s.act === 'number' ? s.act : Number(s.act) || 1 }))
      .filter((s) => s.email);
    const allSigners: SignerItem[] = [...signersFromPlaceholders, ...extraFiltered];
    generateDocForReviewMutation.mutate({
      wordTemplateId: signForm.wordTemplateId,
      nameDocument: signForm.nameDocument.trim(),
      variables: formattedVariables,
      signers: allSigners.map((s) => ({ email: s.email, act: s.act })),
    });
  };

  const deleteConfirmDialog = (
    <Dialog
      open={deleteConfirm !== null}
      onOpenChange={(open) => {
        if (!open) setDeleteConfirm(null);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {deleteConfirm?.kind === 'draft' ? 'Excluir rascunho?' : 'Excluir contrato?'}
          </DialogTitle>
          <DialogDescription>
            {deleteConfirm?.kind === 'draft' ? (
              <>
                O documento no Google Drive <strong>não</strong> é apagado; somente o card sai da lista.
              </>
            ) : (
              <>
                O sistema tenta <strong>cancelar no D4Sign</strong> antes de remover o card. Se o cancelamento
                falhar, nada será excluído.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deletePending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleConfirmDelete} disabled={deletePending}>
            {deletePending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Excluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Tela dedicada: draft (contrato em produção) — card de envio + editor. Apenas aprovadores podem enviar.
  if (selectedDraft?.embedEditUrl) {
    const draft = draftForForm ?? selectedDraft;
    return (
      <>
      <div className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-start gap-2 pb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedDraft(null)}
              aria-label="Voltar para a lista"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg">Enviar este contrato para assinatura</CardTitle>
              <p className="text-sm text-muted-foreground font-normal mt-1">
                Documento: {draft.nameDocument}. {canApprove ? 'Preencha os signatários (e-mail + ação) e o cofre D4Sign para enviar.' : 'Apenas usuários configurados como aprovadores em Integrações > Geração de contratos podem enviar para assinatura.'}
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            {activeSendJobId && sendJobStatus && (
              <div className="space-y-2">
                {(sendJobStatus.status === 'queued' || sendJobStatus.status === 'delayed') && (
                  <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
                    <Clock className="h-4 w-4" />
                    <AlertTitle>Documento na fila</AlertTitle>
                    <AlertDescription className="flex items-center justify-between gap-4">
                      <span>
                        O documento está na fila de envio
                        {sendJobStatus.position != null ? ` (posição ${sendJobStatus.position})` : ''}.
                        Previsão de envio: {formatEstimatedAt(sendJobStatus.estimatedAt) ?? formatEstimatedSend(sendJobStatus.estimatedMinutes)}.
                      </span>
                      {canApprove && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelSendJobMutation.mutate(activeSendJobId)}
                          disabled={cancelSendJobMutation.isPending}
                        >
                          {cancelSendJobMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                          Cancelar envio
                        </Button>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
                {sendJobStatus.status === 'active' && (
                  <Alert className="border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <AlertTitle>Enviando...</AlertTitle>
                    <AlertDescription>
                      O documento está sendo processado e enviado para o D4Sign.
                    </AlertDescription>
                  </Alert>
                )}
                {sendJobStatus.status === 'failed' && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Falha no envio</AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p>{sendJobStatus.error ?? 'Erro desconhecido'}</p>
                      {sendJobStatus.attemptsMade != null && (
                        <p className="text-sm opacity-90">
                          O sistema tentou {sendJobStatus.attemptsMade} vez(es). Todas as retentativas falharam.
                        </p>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => setActiveSendJobId(null)}
                      >
                        Dispensar
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Signatários (ordem preservada)</Label>
                <div className="space-y-2 rounded-lg border p-3 max-h-48 overflow-y-auto">
                  {signForm.signers.map((s, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input
                        type="email"
                        value={s.email}
                        onChange={(e) =>
                          setSignForm((f) => ({
                            ...f,
                            signers: f.signers.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)),
                          }))
                        }
                        placeholder="email@exemplo.com"
                        className="flex-1"
                      />
                      <Select
                        value={String(s.act ?? 1)}
                        onValueChange={(v) =>
                          setSignForm((f) => ({
                            ...f,
                            signers: f.signers.map((x, j) => (j === i ? { ...x, act: Number(v) } : x)),
                          }))
                        }
                      >
                        <SelectTrigger className="w-[220px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SIGNER_ACT_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={String(opt.value)}>
                              {opt.value}. {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setSignForm((f) => ({
                            ...f,
                            signers: f.signers.filter((_, j) => j !== i),
                          }))
                        }
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSignForm((f) => ({ ...f, signers: [...f.signers, { email: '', act: 1 }] }))}
                  >
                    Adicionar signatário
                  </Button>
                </div>
              </div>
              <div>
                <Label>Cofre D4Sign</Label>
                <Select
                  value={signForm.uuidSafe}
                  onValueChange={(v) => setSignForm((f) => ({ ...f, uuidSafe: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cofre" />
                  </SelectTrigger>
                  <SelectContent>
                    {safes.map((s) => (
                      <SelectItem key={s.uuid_safe} value={s.uuid_safe}>
                        {(s as { uuid_safe: string; 'name-safe'?: string })['name-safe'] ?? s.uuid_safe}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="draft-message">Mensagem (opcional)</Label>
                <Input
                  id="draft-message"
                  value={signForm.message}
                  onChange={(e) => setSignForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder="Mensagem enviada aos signatários"
                />
              </div>
              <div className="md:col-span-2 flex gap-2 flex-wrap items-center">
                <Button variant="outline" onClick={() => setSelectedDraft(null)}>
                  Voltar para a lista
                </Button>
                <ContractDocumentActions draft={draft} size="sm" showDownload={false} />
                {canDeleteDraftRow(draft) && (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive border-destructive/50 hover:bg-destructive/10"
                    onClick={() =>
                      setDeleteConfirm({
                        kind: 'draft',
                        id: draft.id,
                        name: draft.nameDocument,
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Excluir rascunho
                  </Button>
                )}
                {canApprove && (
                  <Button
                    onClick={handleSendDraft}
                    disabled={
                      !signForm.uuidSafe ||
                      signForm.signers.filter((s) => s.email?.trim()).length === 0 ||
                      sendForSignatureMutation.isPending
                    }
                  >
                    {sendForSignatureMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Enviar para assinatura
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <GoogleDocsEmbedEditor
          embedEditUrl={draft.embedEditUrl!}
          title={`Editar: ${draft.nameDocument}`}
          onClose={() => setSelectedDraft(null)}
          showLayoutOptions
          userEmail={user?.email}
        />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Histórico do contrato</CardTitle>
          </CardHeader>
          <CardContent>
            {draftLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sem movimentações registradas.</p>
            ) : (
              <div className="space-y-2">
                {draftLogs.map((log) => (
                  <div key={log.id} className="rounded-md border p-2">
                    <p className="text-sm font-medium">{getLogActionLabel(log.action)}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(log.createdAt).toLocaleString()} • {log.createdByEmail ?? 'Sistema'}
                    </p>
                    {log.message && <p className="text-sm mt-1">{log.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {deleteConfirmDialog}
      </>
    );
  }

  // Tela dedicada: preencher formulário (novo documento)
  if (fillFormScreenOpen) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setFillFormScreenOpen(false)} aria-label="Voltar">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <CardTitle>Preencher formulário</CardTitle>
              <p className="text-sm text-muted-foreground font-normal mt-1">
                Selecione o modelo, preencha os campos e gere o documento. Os aprovadores (configurados em Integrações &gt; Geração de contratos) serão notificados e poderão enviar para assinatura no D4Sign.
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Modelo Word</Label>
              <Select
                value={signForm.wordTemplateId}
                onValueChange={(v) => setSignForm((f) => ({ ...f, wordTemplateId: v, variables: {} }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o modelo" />
                </SelectTrigger>
                <SelectContent>
                  {wordTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="fill-name-doc">Nome do documento</Label>
              <Input
                id="fill-name-doc"
                value={signForm.nameDocument}
                onChange={(e) => setSignForm((f) => ({ ...f, nameDocument: e.target.value }))}
                placeholder="Ex: CONTRATO ARCO - Cliente X"
              />
            </div>
            <div className="space-y-2">
              <Label>Formulário (campos do documento)</Label>
              {loadingTemplatePlaceholders ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Buscando campos...
                </div>
              ) : placeholderKeys.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Nenhum placeholder no documento. Selecione um modelo.</p>
              ) : (
                <div className="grid gap-3 max-h-[50vh] overflow-y-auto">
                  {(() => {
                    const mergedVariables = getVariablesWithComputed(
                      signForm.variables,
                      selectedWordTemplate?.placeholderConfig ?? undefined,
                      placeholderKeys,
                    );
                    return placeholderKeys.map((key) => (
                      <PlaceholderInput
                        key={key}
                        placeholderKey={key}
                        config={selectedWordTemplate?.placeholderConfig?.[key]}
                        value={mergedVariables[key] ?? ''}
                        onChange={(value) =>
                          setSignForm((f) => ({ ...f, variables: { ...f.variables, [key]: value } }))
                        }
                        id={`fill-var-${key}`}
                      />
                    ));
                  })()}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>E-mails extras para assinatura (opcional)</Label>
              <p className="text-xs text-muted-foreground">
                Adicione signatários que não vêm dos campos do formulário. A ordem é preservada na assinatura.
              </p>
              <div className="space-y-2 rounded-lg border p-3 max-h-40 overflow-y-auto">
                {extraSigners.map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input
                      type="email"
                      value={s.email}
                      onChange={(e) =>
                        setExtraSigners((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, email: e.target.value } : x))
                        )
                      }
                      placeholder="email@exemplo.com"
                      className="flex-1"
                    />
                    <Select
                      value={String(s.act ?? 1)}
                      onValueChange={(v) =>
                        setExtraSigners((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, act: Number(v) } : x))
                        )
                      }
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SIGNER_ACT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={String(opt.value)}>
                            {opt.value}. {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setExtraSigners((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setExtraSigners((prev) => [...prev, { email: '', act: 1 }])}
                >
                  Adicionar signatário extra
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setFillFormScreenOpen(false)}>Voltar</Button>
              <Button
                onClick={handleGenerateForReview}
                disabled={
                  !signForm.wordTemplateId ||
                  !signForm.nameDocument.trim() ||
                  generateDocForReviewMutation.isPending
                }
              >
                {generateDocForReviewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Gerar documento
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Gestão de Contratos</h1>
          <p className="text-muted-foreground">
            Modelo no Google Docs com placeholders → formulário → PDF enviado para assinatura no D4Sign.
          </p>
        </div>
        {config.google.clientId && !googleAccessToken && onGoogleLogin && (
          <Button variant="outline" onClick={() => onGoogleLogin()}>
            Entrar com Google
          </Button>
        )}
      </div>

      {activeSendJobId && sendJobStatus && (
        <Alert
          variant={sendJobStatus.status === 'failed' ? 'destructive' : 'default'}
          className={
            sendJobStatus.status === 'queued' || sendJobStatus.status === 'delayed'
              ? 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30'
              : sendJobStatus.status === 'active'
                ? 'border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/30'
                : undefined
          }
        >
          {(sendJobStatus.status === 'queued' || sendJobStatus.status === 'delayed') && (
            <>
              <Clock className="h-4 w-4" />
              <AlertTitle>Documento na fila</AlertTitle>
              <AlertDescription className="flex items-center justify-between gap-4 flex-wrap">
                <span>
                  O documento está na fila
                  {sendJobStatus.position != null ? ` (posição ${sendJobStatus.position})` : ''}.
                  {' '}
                  Previsão de envio: {formatEstimatedAt(sendJobStatus.estimatedAt) ?? formatEstimatedSend(sendJobStatus.estimatedMinutes)}.
                </span>
                {canApprove && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => cancelSendJobMutation.mutate(activeSendJobId)}
                    disabled={cancelSendJobMutation.isPending}
                  >
                    {cancelSendJobMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    Cancelar
                  </Button>
                )}
              </AlertDescription>
            </>
          )}
          {sendJobStatus.status === 'active' && (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertTitle>Enviando...</AlertTitle>
              <AlertDescription>O documento está sendo processado e enviado para o D4Sign.</AlertDescription>
            </>
          )}
          {sendJobStatus.status === 'failed' && (
            <>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Falha no envio</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>{sendJobStatus.error ?? 'Erro desconhecido'}</p>
                {sendJobStatus.attemptsMade != null && (
                  <p className="text-sm opacity-90">
                    O sistema tentou {sendJobStatus.attemptsMade} vez(es). Todas as retentativas falharam.
                  </p>
                )}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setActiveSendJobId(null)}>
                  Dispensar
                </Button>
              </AlertDescription>
            </>
          )}
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="contracts">Contratos</TabsTrigger>
          <TabsTrigger value="word">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="space-y-4">
          <Card>
            <CardHeader className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle>Contratos</CardTitle>
                <Button
                  onClick={() => setFillFormScreenOpen(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                >
                  <FilePlus className="h-4 w-4 mr-2" />
                  Novo contrato
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Lista de contratos em produção (gerados pelo formulário) e enviados para assinatura. Os documentos são salvos nas pastas configuradas no servidor (.env). Entre com o Google para visualizar e editar os documentos no editor.
              </p>
              {!googleAccessToken && config.google.clientId && (
                <p className="text-muted-foreground text-sm py-2">Entre com o Google para visualizar e editar os documentos no editor.</p>
              )}
            </CardHeader>
            <CardContent>
              {/* Abas internas de filtragem por origem/status */}
              <div className="flex flex-wrap items-center gap-x-1 gap-y-1 mb-4 border-b pb-3">
                {(
                  [
                    { key: 'all',     label: 'Todos' },
                    { key: 'drafts',  label: 'Em produção' },
                    { key: 'signing', label: 'Aguardando assinatura' },
                    { key: 'signed',  label: 'Assinados' },
                  ] as const
                ).map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setContractsInnerTab(key);
                      setContractsPage(1);
                    }}
                    className={[
                      'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                      contractsInnerTab === key
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex flex-wrap gap-2">
                  <Input
                    placeholder="Buscar por nome do documento..."
                    value={contractsSearchInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setContractsSearchInput(value);
                      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                      searchDebounceRef.current = setTimeout(() => {
                        setContractsPage(1);
                        setContractsSearch((prev) => {
                          if (prev === value) return prev;
                          return value;
                        });
                      }, 1000);
                    }}
                    className="max-w-xs"
                  />
                  <Select
                    value={contractsShowDeleted}
                    onValueChange={(v: 'active' | 'deleted' | 'all') => {
                      setContractsShowDeleted(v);
                      setContractsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Excluídos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Somente ativos</SelectItem>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="deleted">Somente excluídos</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={selectedTagFilterId}
                    onValueChange={(value) => {
                      setSelectedTagFilterId(value);
                      setContractsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Filtrar por tag" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as tags</SelectItem>
                      {contractTags.map((tag) => (
                        <SelectItem key={tag.id} value={tag.id}>
                          {tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="date"
                    value={contractsStartDate}
                    onChange={(e) => {
                      const nextStart = e.target.value;
                      setContractsStartDate(nextStart);
                      setContractsEndDate((prevEnd) => {
                        if (!nextStart || !prevEnd) return prevEnd;
                        return nextStart > prevEnd ? nextStart : prevEnd;
                      });
                      setContractsPage(1);
                    }}
                    className="w-[160px]"
                    title="Data inicial"
                  />
                  <Input
                    type="date"
                    value={contractsEndDate}
                    onChange={(e) => {
                      const nextEnd = e.target.value;
                      setContractsEndDate(nextEnd);
                      setContractsStartDate((prevStart) => {
                        if (!nextEnd || !prevStart) return prevStart;
                        return nextEnd < prevStart ? nextEnd : prevStart;
                      });
                      setContractsPage(1);
                    }}
                    className="w-[160px]"
                    title="Data final"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setTagManagerDialogOpen(true)}
                  >
                    Gerenciar tags
                  </Button>
                </div>
              </div>

              {(loadingContracts || loadingDrafts) ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando...
                </div>
              ) : !contractsPageData || contractsPageData.items.length === 0 ? (
                <p className="text-muted-foreground">
                  Nenhum contrato. Use &quot;Preencher formulário&quot; para criar um documento e enviá-lo para assinatura.
                </p>
              ) : contractsInnerTab === 'all' ? (
                <div className="grid grid-cols-4 gap-4">
                  {KANBAN_COLUMNS.map((column) => {
                    const items = contractsPageData.items.filter(
                      (item) => getKanbanColumnFromItem(item) === column.key,
                    );
                    return (
                      <div key={column.key} className="rounded-lg border bg-muted/20">
                        <div className="px-3 py-2 border-b flex items-center justify-between">
                          <h3 className="font-medium text-sm">{column.label}</h3>
                          <Badge variant="secondary">{items.length}</Badge>
                        </div>
                        <div className="p-3 space-y-3 min-h-[220px]">
                          {items.length === 0 ? (
                            <p className="text-xs text-muted-foreground">Sem contratos nesta coluna.</p>
                          ) : (
                            items.map((item) => {
                              const currentColumn = getKanbanColumnFromItem(item);
                              const tags = item.kind === 'draft' ? item.draft.tags ?? [] : item.contract.tags ?? [];
                              const firstTagColor = tags[0]?.color;
                              const itemCreatedAt =
                                item.kind === 'draft' ? item.draft.createdAt : item.contract.createdAt;
                              const d4signContract =
                                item.kind === 'd4sign' ? item.contract : null;
                              const clientContractItem = d4signContract?.clientContractId
                                ? clientContractsList.find(
                                    (cc) => cc.contract.id === d4signContract.clientContractId,
                                  )
                                : null;
                              const employeeContractItem = d4signContract?.employeeContractId
                                ? employeeContractsList.find(
                                    (ec) => ec.contract.id === d4signContract.employeeContractId,
                                  )
                                : null;
                              const clientContractLabel = clientContractItem
                                ? `${getClientDisplayName(clientContractItem.client)} (${clientContractItem.contract.contractType})`
                                : null;
                              const employeeContractLabel = employeeContractItem
                                ? `${employeeContractItem.professionalData?.fullName || employeeContractItem.user.email} (${employeeContractItem.contract.contractType})`
                                : null;
                              return (
                                <div
                                  key={`${item.kind}-${item.kind === 'draft' ? item.draft.id : item.contract.id}`}
                                  className="rounded-lg border bg-background p-3 space-y-2 cursor-pointer hover:bg-muted/30 transition-colors"
                                  style={
                                    firstTagColor
                                      ? {
                                          borderColor: `${firstTagColor}66`,
                                          boxShadow: `0 0 0 1px ${firstTagColor}22`,
                                        }
                                      : undefined
                                  }
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    if (item.kind === 'draft') {
                                      scrollBeforeDraftRef.current = window.scrollY;
                                      setSelectedDraft(item.draft);
                                      return;
                                    }
                                    openContractDetail(item.contract);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    if (item.kind === 'draft') {
                                      scrollBeforeDraftRef.current = window.scrollY;
                                      setSelectedDraft(item.draft);
                                      return;
                                    }
                                    openContractDetail(item.contract);
                                  }}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="font-medium text-sm whitespace-normal break-words leading-snug">
                                        {item.kind === 'draft' ? item.draft.nameDocument : item.contract.nameDoc}
                                      </p>
                                      {item.kind === 'd4sign' && (
                                        <p className="text-xs text-muted-foreground">
                                          Status D4Sign: {item.contract.statusName ?? '-'}
                                        </p>
                                      )}
                                      {d4signContract && isContractPartialSignature(d4signContract) && (
                                        <Badge variant="outline" className="text-amber-700 border-amber-600 text-xs mt-1">
                                          Assinatura parcial
                                        </Badge>
                                      )}
                                      {clientContractLabel && (
                                        <Badge variant="outline" className="text-emerald-700 border-emerald-600 text-xs mt-1">
                                          Cliente: {clientContractLabel}
                                        </Badge>
                                      )}
                                      {employeeContractLabel && (
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                          Func.: {employeeContractLabel}
                                        </p>
                                      )}
                                      <p className="text-xs text-muted-foreground">
                                        Criado em:{' '}
                                        {itemCreatedAt
                                          ? new Date(itemCreatedAt).toLocaleString()
                                          : '-'}
                                      </p>
                                    </div>
                                    {renderItemActionsMenu(item, {
                                      showContaAzulAction:
                                        !!d4signContract &&
                                        currentColumn === 'assinado',
                                      onDownloadComplete: d4signContract
                                        ? () =>
                                            queryClient.invalidateQueries({
                                              queryKey: [
                                                'contract-management-contract-logs',
                                                d4signContract.id,
                                              ],
                                            })
                                        : undefined,
                                    })}
                                  </div>

                                  <div className="flex flex-wrap gap-1">
                                    {tags.map((tag) => (
                                      <Badge
                                        key={tag.id}
                                        variant="outline"
                                        className="cursor-pointer"
                                        style={{ borderColor: tag.color, color: tag.color }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeTagMutation.mutate({ item, tagId: tag.id });
                                        }}
                                      >
                                        {tag.name} ×
                                      </Badge>
                                    ))}
                                  </div>

                                  {d4signContract &&
                                    isContractSignedOrPartial(d4signContract) &&
                                    currentColumn === 'assinado' && (
                                    <div className="flex items-center justify-between">
                                      {d4signContract.contaAzulRegisteredAt && (
                                        <Badge variant="outline" className="text-emerald-700 border-emerald-600">
                                          Conta Azul cadastrado
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Documento</TableHead>
                        <TableHead>Tipo / Status</TableHead>
                        <TableHead>Criado em</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contractsPageData.items.map((item: ContractListItem) => {
                        if (item.kind === 'draft') {
                          const d = item.draft;
                          const isDeleted = !!d.deletedAt;
                          return (
                            <TableRow
                              key={`draft-${d.id}`}
                              className={`cursor-pointer hover:bg-muted/50 ${isDeleted ? 'opacity-60' : ''}`}
                              onClick={() => {
                                if (isDeleted) return;
                                scrollBeforeDraftRef.current = window.scrollY;
                                setSelectedDraft(d);
                              }}
                            >
                              <TableCell>
                                <div className="space-y-1">
                                  <div>{d.nameDocument}</div>
                                  <div className="flex flex-wrap gap-1">
                                    {(d.tags ?? []).map((tag) => (
                                      <Badge
                                        key={tag.id}
                                        variant="outline"
                                        style={{ borderColor: tag.color, color: tag.color }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeTagMutation.mutate({ item, tagId: tag.id });
                                        }}
                                      >
                                        {tag.name} ×
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                Em produção
                              </TableCell>
                              <TableCell className="text-muted-foreground text-sm">
                                {d.createdAt ? new Date(d.createdAt).toLocaleString() : '-'}
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-end">
                                  {renderItemActionsMenu(item)}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        const c = item.contract;
                        const isDeleted = !!c.deletedAt;
                        const clientContractItem = c.clientContractId ? clientContractsList.find((item) => item.contract.id === c.clientContractId) : null;
                        const employeeContractItem = c.employeeContractId ? employeeContractsList.find((i) => i.contract.id === c.employeeContractId) : null;
                        const clientContractLabel = clientContractItem ? `${getClientDisplayName(clientContractItem.client)} (${clientContractItem.contract.contractType})` : null;
                        const employeeContractLabel = employeeContractItem ? `${employeeContractItem.professionalData?.fullName || employeeContractItem.user.email} (${employeeContractItem.contract.contractType})` : null;
                        return (
                          <TableRow
                            key={`contract-${c.id}`}
                            className={`cursor-pointer hover:bg-muted/50 ${isDeleted ? 'opacity-60' : ''}`}
                            onClick={() => openContractDetail(c)}
                          >
                            <TableCell>
                              <div className="space-y-1">
                                <div>{c.nameDoc}</div>
                                <div className="flex flex-wrap gap-1">
                                  {(c.tags ?? []).map((tag) => (
                                    <Badge
                                      key={tag.id}
                                      variant="outline"
                                      style={{ borderColor: tag.color, color: tag.color }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeTagMutation.mutate({ item, tagId: tag.id });
                                      }}
                                    >
                                      {tag.name} ×
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              Enviado D4Sign · {c.statusName ?? '-'}
                              {((c.signedByEmails?.length ?? 0) > 0 || c.signedByEmail) && (
                                <>
                                  {' '}
                                  · Assinado por:{' '}
                                  {(c.signedByEmails?.length ?? 0) > 0
                                    ? c.signedByEmails!.join(', ')
                                    : c.signedByEmail}
                                </>
                              )}
                              {(clientContractLabel || employeeContractLabel) && (
                                <>
                                  <br />
                                  <span className="text-xs">
                                    {clientContractLabel && <>Contrato cliente: {clientContractLabel}</>}
                                    {clientContractLabel && employeeContractLabel && ' · '}
                                    {employeeContractLabel && <>Contrato func.: {employeeContractLabel}</>}
                                  </span>
                                </>
                              )}
                              {isContractPartialSignature(c) && (
                                <>
                                  <br />
                                  <Badge variant="outline" className="border-amber-600 text-amber-700 text-xs">
                                    Assinatura parcial
                                  </Badge>
                                </>
                              )}
                              {contractsInnerTab === 'signed' && isContractSignedOrPartial(c) && (
                                <>
                                  <br />
                                  {c.contaAzulRegisteredAt ? (
                                    <Badge variant="outline" className="border-emerald-600 text-emerald-700">
                                      <CheckCircle2 className="h-3 w-3 mr-1" />
                                      Cadastrado no Conta Azul
                                    </Badge>
                                  ) : null}
                                </>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {c.createdAt ? new Date(c.createdAt).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end">
                                {renderItemActionsMenu(item, {
                                  showContaAzulAction: contractsInnerTab === 'signed',
                                  onDownloadComplete: () =>
                                    queryClient.invalidateQueries({
                                      queryKey: ['contract-management-contract-logs', c.id],
                                    }),
                                })}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
                    <span>
                      Página {contractsPage} de {contractsPageData.totalPages} ({contractsPageData.total}{' '}
                      contratos)
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={contractsPage <= 1}
                        onClick={() => setContractsPage((p) => Math.max(1, p - 1))}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={contractsPage >= contractsPageData.totalPages}
                        onClick={() =>
                          setContractsPage((p) =>
                            Math.min(contractsPageData.totalPages, p + 1),
                          )
                        }
                      >
                        Próxima
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="word" className="space-y-4">
          <Card>
            <CardHeader className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle>Templates</CardTitle>
                <Button
                  onClick={() => navigate({ to: '/contract-management/templates/new' })}
                  className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                >
                  <FilePlus className="h-4 w-4 mr-2" />
                  Criar novo template
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Modelos são documentos no Google Docs com placeholders (ex.: $&#123;NOME&#125;). O documento é criado na pasta configurada no servidor (.env). Entre com o Google para visualizar e editar no editor embedado.
              </p>
            </CardHeader>
            <CardContent>
              {loadingWord ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando...
                </div>
              ) : wordTemplates.length === 0 ? (
                <p className="text-muted-foreground">Nenhum template. Clique em &quot;Criar novo template&quot; para criar um documento na pasta configurada no servidor.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Placeholders</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wordTemplates.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>{t.name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {t.targetType === 'employee' ? 'Funcionários' : t.targetType === 'client' ? 'Clientes' : 'Ambos'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {(t.placeholders ?? []).slice(0, 5).join(', ')}
                          {(t.placeholders?.length ?? 0) > 5 ? '...' : ''}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                navigate({
                                  to: '/contract-management/templates/$id',
                                  params: { id: t.id },
                                })
                              }
                              title="Editar template"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteWordMutation.mutate(t.id)}
                              disabled={deleteWordMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

            <Dialog open={tagManagerDialogOpen} onOpenChange={setTagManagerDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gerenciar tags</DialogTitle>
            <DialogDescription>
              Crie novas tags e veja o histórico das tags existentes com o criador.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border p-3 space-y-3">
              <p className="text-sm font-medium">Nova tag</p>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Nome da tag"
                  className="max-w-[220px]"
                />
                <Input
                  type="color"
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  className="w-12 p-1"
                />
                <Button
                  onClick={() =>
                    createTagMutation.mutate({
                      name: newTagName.trim(),
                      color: newTagColor,
                    })
                  }
                  disabled={!newTagName.trim() || createTagMutation.isPending}
                >
                  Criar tag
                </Button>
              </div>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tag</TableHead>
                    <TableHead>Criador</TableHead>
                    <TableHead>Criada em</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contractTags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        Nenhuma tag cadastrada.
                      </TableCell>
                    </TableRow>
                  ) : (
                    contractTags.map((tag) => (
                      <TableRow key={tag.id}>
                        <TableCell>
                          <Badge variant="outline" style={{ borderColor: tag.color, color: tag.color }}>
                            {tag.name}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {tag.createdByEmail ?? tag.createdBy ?? 'Sistema'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(tag.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Excluir tag ${tag.name}`}
                            onClick={async () => {
                              if (
                                await confirm({
                                  title: 'Excluir tag?',
                                  description: `A tag "${tag.name}" será removida de todos os contratos.`,
                                  confirmLabel: 'Excluir tag',
                                  destructive: true,
                                })
                              ) {
                                deleteTagMutation.mutate(tag.id);
                              }
                            }}
                            disabled={deleteTagMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addTagDialogOpen}
        onOpenChange={(open) => {
          setAddTagDialogOpen(open);
          if (!open) {
            setAddTagTargetItem(null);
            setAddTagSelectedTagId('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar tag ao contrato</DialogTitle>
            <DialogDescription>
              {addTagTargetItem
                ? `Selecione a tag para ${
                    addTagTargetItem.kind === 'draft'
                      ? addTagTargetItem.draft.nameDocument
                      : addTagTargetItem.contract.nameDoc
                  }.`
                : 'Selecione a tag.'}
            </DialogDescription>
          </DialogHeader>

          {addTagAvailableTags.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Não há tags disponíveis para este contrato.
            </p>
          ) : (
            <div className="space-y-3">
              <Select
                value={addTagSelectedTagId || 'none'}
                onValueChange={(value) =>
                  setAddTagSelectedTagId(value === 'none' ? '' : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Selecione...</SelectItem>
                  {addTagAvailableTags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddTagDialogOpen(false);
                setAddTagTargetItem(null);
                setAddTagSelectedTagId('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmAddTag}
              disabled={!addTagSelectedTagId || addTagMutation.isPending}
            >
              Adicionar tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!contaAzulConfirmContract}
        onOpenChange={(open) => !open && setContaAzulConfirmContract(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmar marcação no Conta Azul</DialogTitle>
            <DialogDescription>
              O status atual está como <strong>Signed</strong> (assinatura parcial). Confirme no
              Doc4Sign se o documento está totalmente assinado antes de marcar como cadastrado no
              Conta Azul.
              <br />
              Deseja confirmar a marcação?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setContaAzulConfirmContract(null)}
              disabled={markContaAzulMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (!contaAzulConfirmContract) return;
                markContaAzulMutation.mutate({
                  contractId: contaAzulConfirmContract.id,
                  confirmPartialSignature: true,
                });
              }}
              disabled={markContaAzulMutation.isPending}
            >
              {markContaAzulMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Confirmar e marcar no Conta Azul
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {deleteConfirmDialog}

    </div>
  );
}
